/**
 * Shared email composer component.
 * Used by cadence step drawer AND bulk message content step.
 *
 * Renders: subject input, RichTextEditor, toolbar (file upload, signature, template),
 * uploaded files list, and signature/template modals.
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@evertrue/et-components';
import { RichTextEditor } from '@evertrue/sdk';
import { ModalTrigger } from '@evertrue/et-components';
import EmailTemplatesModalController from 'apps/outreach/email/templates/email-templates-modal-controller';
import EmailSignaturesModal from 'apps/outreach/email/signatures/components/email-signatures-modal';
import FileUpload from 'components/emailer/components/file-upload';
import UploadedFiles from 'components/emailer/components/uploaded-files';
import { DYNAMIC_FIELDS, MAX_AUTOMATED_ATTACHMENT_BYTES } from '../../utils/cadence-constants';

/**
 * @param {Object} props
 * @param {string} props.subject - Current subject line value
 * @param {Function} props.onSubjectChange - Called with new subject string
 * @param {string} props.body - Current body HTML value
 * @param {Function} props.onBodyChange - Called with new body HTML string
 * @param {Function} props.onTemplateApply - Called with { id, templateName } when a template is selected
 * @param {Array} props.files - Current uploaded files array (from useFileUploader)
 * @param {Function} props.removeFile - Remove file handler (from useFileUploader)
 * @param {Function} props.onImageUpload - Image upload handler (from useFileUploader)
 * @param {Function} props.onFileUpload - File upload handler (from useFileUploader)
 * @param {Array} [props.outreachTemplates] - Available outreach templates for template picker
 * @param {string} [props.subjectPlaceholder] - Placeholder for subject input
 * @param {string} [props.bodyPlaceholder] - Placeholder for body editor
 * @param {string} [props.editorHeight] - RTE height (default '200px')
 * @param {string} [props.editorKey] - Key to force RTE remount (for edit vs create)
 * @param {string} [props.classPrefix] - CSS class prefix (default 'create-cadence-drawer')
 */
const EmailComposer = ({
  subject,
  onSubjectChange,
  body,
  onBodyChange,
  onTemplateApply,
  files,
  removeFile,
  onImageUpload,
  onFileUpload,
  outreachTemplates,
  subjectPlaceholder = 'Email subject line',
  bodyPlaceholder = 'Compose your email...',
  editorHeight = '200px',
  editorKey,
  classPrefix = 'create-cadence-drawer',
}) => {
  const [sigModalOpen, setSigModalOpen] = useState(false);

  // Bulk messages always go via Postmark — compute which non-inline files pushed the
  // running total over the 7MB limit. Files are still uploaded and included in the
  // payload so the backend returns ATTACHMENT_TOO_LARGE if the user proceeds.
  const overLimitMap = (() => {
    let running = 0;
    const map = new Map();
    for (const f of files.filter((f) => !f.isInline)) {
      running += f.file?.size || 0;
      if (running > MAX_AUTOMATED_ATTACHMENT_BYTES) map.set(f.id, running);
    }
    return map;
  })();
  const validDisplayFiles = files.filter((f) => !overLimitMap.has(f.id));
  const overLimitFiles = files.filter((f) => overLimitMap.has(f.id));

  return (
    <>
      <div className={`${classPrefix}--field`}>
        <label className={`${classPrefix}--label`}>Subject</label>
        <input
          type="text"
          className={`${classPrefix}--input`}
          placeholder={subjectPlaceholder}
          value={subject || ''}
          onChange={(e) => onSubjectChange(e.target.value)}
        />
      </div>

      <div className={`${classPrefix}--field`}>
        <label className={`${classPrefix}--label`}>Body</label>
        <RichTextEditor
          key={editorKey}
          value={body || ''}
          onChange={onBodyChange}
          dynamicFields={DYNAMIC_FIELDS}
          onImageUpload={onImageUpload}
          onFileUpload={onFileUpload}
          placeholder={bodyPlaceholder}
          additionalConfig={{ height: editorHeight, toolbarAdaptive: false }}
        />
      </div>

      <div className={`${classPrefix}--email-toolbar`}>
        <FileUpload onFileChange={onFileUpload} allowMultiple={true} />
        <button
          type="button"
          className={`${classPrefix}--toolbar-btn`}
          onClick={() => setSigModalOpen(true)}
          title="Add Signature"
        >
          <Icon icon="email-signature-icon" iconKit="gt2" />
        </button>
        <ModalTrigger
          size="1060px"
          closeOnOutsideClick={true}
          buttonProps={{ type: 'simple' }}
          buttonText={<Icon icon="email-template" iconKit="gt2" />}
        >
          {({ closeModal }) => (
            <EmailTemplatesModalController
              closeModal={closeModal}
              setFieldValue={(field, val) => {
                if (field === 'body') onBodyChange(val);
                if (field === 'subject') onSubjectChange(val);
                if (field === 'email_template_id' && onTemplateApply) {
                  const tmpl = (outreachTemplates || []).find((t) => String(t.id) === String(val));
                  onTemplateApply({ id: val, templateName: tmpl?.templateName || 'Selected Template' });
                }
              }}
              emailBody={body || ''}
            />
          )}
        </ModalTrigger>
      </div>

      {files.some((file) => !file.isInline) && (
        <div className={`${classPrefix}--attachments`}>
          <UploadedFiles files={validDisplayFiles} removeFile={removeFile} />
          {overLimitFiles.length > 0 && (
            <div className="uploaded-files--container">
              {overLimitFiles.map((f) => (
                <div key={f.id} className="uploaded-file uploaded-file--error">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Icon icon="alert-circle" style={{ color: '#c0392b' }} />
                    <div style={{ marginLeft: '10px' }}>
                      <p>{f.file?.name}</p>
                      <small style={{ color: '#c0392b' }}>
                        Attachment warning: Total attachment size exceeds the 7MB limit (
                        {(overLimitMap.get(f.id) / 1_000_000).toFixed(1)} MB total).
                      </small>
                    </div>
                  </div>
                  <Icon
                    icon="close"
                    onClick={() => removeFile(f.id)}
                    isReachable={true}
                    onKeyUp={() => removeFile(f.id)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <EmailSignaturesModal
        isOpen={sigModalOpen}
        onClose={() => setSigModalOpen(false)}
        setFieldValue={(field, val) => {
          if (field === 'body') onBodyChange(val);
          if (field === 'subject') onSubjectChange(val);
        }}
        emailBody={body || ''}
      />
    </>
  );
};

EmailComposer.propTypes = {
  subject: PropTypes.string,
  onSubjectChange: PropTypes.func.isRequired,
  body: PropTypes.string,
  onBodyChange: PropTypes.func.isRequired,
  onTemplateApply: PropTypes.func,
  files: PropTypes.array.isRequired,
  removeFile: PropTypes.func.isRequired,
  onImageUpload: PropTypes.func.isRequired,
  onFileUpload: PropTypes.func.isRequired,
  outreachTemplates: PropTypes.array,
  subjectPlaceholder: PropTypes.string,
  bodyPlaceholder: PropTypes.string,
  editorHeight: PropTypes.string,
  editorKey: PropTypes.string,
  classPrefix: PropTypes.string,
};

export default EmailComposer;
